import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Plus, Upload, Image as ImageIcon, Download, RefreshCw, AlertTriangle, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import AquaDiversCompareCard from './AquaDiversCompareCard';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CatalogSyncTab = ({ token }) => {
  const [catalogComparison, setCatalogComparison] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogSyncResult, setCatalogSyncResult] = useState(null);
  const catalogFileRef = useRef(null);
  const [selectedNewProducts, setSelectedNewProducts] = useState(new Set());
  const [selectedDiffProducts, setSelectedDiffProducts] = useState(new Set());
  const [catalogImageUploading, setCatalogImageUploading] = useState(null);
  const [catalogDetailView, setCatalogDetailView] = useState('differences');

  const handleCatalogCompare = async (file) => {
    if (!file) return;
    setCatalogLoading(true);
    setCatalogComparison(null);
    setCatalogSyncResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/admin/products/compare-excel`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });
      setCatalogComparison(res.data);
      toast.success(`Comparison complete: ${res.data.summary.total_matched} matched, ${res.data.summary.total_excel_only} new products found`);
    } catch (error) {
      console.error('Catalog compare error:', error);
      const msg = error.response?.data?.detail 
        || (error.code === 'ERR_NETWORK' ? 'Network error - file may be too large. Try a smaller file.' : '')
        || (error.code === 'ECONNABORTED' ? 'Request timed out - file may be too large.' : '')
        || error.message 
        || 'Failed to compare catalog';
      toast.error(msg);
    } finally {
      setCatalogLoading(false);
    }
  };

  const handleCatalogSync = async (createNew, updateExisting) => {
    if (!catalogFileRef.current?.files?.[0]) {
      toast.error('Please select an Excel file first');
      return;
    }
    setCatalogLoading(true);
    const formData = new FormData();
    formData.append('file', catalogFileRef.current.files[0]);
    try {
      const res = await axios.post(
        `${API_URL}/admin/products/sync-from-excel?create_new=${createNew}&update_existing=${updateExisting}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setCatalogSyncResult(res.data);
      toast.success(`Sync complete: ${res.data.summary.created} created, ${res.data.summary.updated} updated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync catalog');
    } finally {
      setCatalogLoading(false);
    }
  };

  const handleCatalogImageUpload = async (productId, file) => {
    if (!file || !productId) return;
    setCatalogImageUploading(productId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/admin/products/${productId}/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (catalogComparison) {
        const updated = { ...catalogComparison };
        ['matched', 'differences', 'db_only'].forEach(key => {
          updated[key] = updated[key].map(item =>
            (item.db_id || item.id) === productId ? { ...item, has_image: true, image_url: res.data.image_url } : item
          );
        });
        setCatalogComparison(updated);
      }
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setCatalogImageUploading(null);
    }
  };

  // Brand-crawl bulk import (Aqualung etc.)
  const [importLoading, setImportLoading] = useState({});
  const [importResult, setImportResult] = useState({});

  const handleImportFromCrawl = async (brand) => {
    if (!window.confirm(`Import ${brand} products into the database? Any existing ${brand} products will be REPLACED. Use this after a fresh deploy to load the crawled catalog.`)) return;
    setImportLoading(prev => ({ ...prev, [brand]: true }));
    setImportResult(prev => ({ ...prev, [brand]: null }));
    try {
      const res = await axios.post(
        `${API_URL}/admin/products/import-from-json`,
        { json_path: `/app/frontend/public/uploads/${brand.toLowerCase()}_products.json`, replace_brand: brand },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      setImportResult(prev => ({ ...prev, [brand]: res.data }));
      toast.success(res.data.message || 'Import complete');
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Import failed';
      toast.error(`${brand} import failed: ${msg}`);
    } finally {
      setImportLoading(prev => ({ ...prev, [brand]: false }));
    }
  };

  const BRAND_CARDS = [
    { brand: 'Aqualung', count: 278, color: 'orange' },
    { brand: 'Apeks', count: 113, color: 'blue' },
  ];

  return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold" data-testid="catalog-sync-title">Catalog Sync - Excel to Database</h2>
            </div>

            {/* Live Aqua Divers stock comparison */}
            <AquaDiversCompareCard token={token} />

            {/* Brand Crawl Import — one card per brand crawled from Manex */}
            {BRAND_CARDS.map(({ brand, count, color }) => {
              const isOrange = color === 'orange';
              const bgClass = isOrange ? 'from-orange-50 border-orange-200' : 'from-blue-50 border-blue-200';
              const lowerBrand = brand.toLowerCase();
              return (
                <div key={brand} className={`bg-gradient-to-br ${bgClass} to-white border rounded-lg p-6`} data-testid={`brand-import-card-${lowerBrand}`}>
                  <h3 className="text-md font-semibold mb-2 flex items-center gap-2 text-[#0A2342]">
                    <Plus className={`w-5 h-5 ${isOrange ? 'text-[#FF6B35]' : 'text-blue-600'}`} />
                    Brand Catalog Import — {brand}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {count} {brand} products from Manex catalog are bundled with this deploy.
                    Click <strong>Import to Database</strong> to load them — any existing {brand} products are replaced first (idempotent).
                    Pricing is Manex cost &times; 1.20 (20% markup).
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Button
                      onClick={() => handleImportFromCrawl(brand)}
                      disabled={importLoading[brand]}
                      className={isOrange ? "bg-[#FF6B35] hover:bg-[#FF6B35]/90" : "bg-blue-600 hover:bg-blue-700"}
                      data-testid={`brand-import-btn-${lowerBrand}`}
                    >
                      {importLoading[brand] ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</>) : (<><RefreshCw className="w-4 h-4 mr-2" />Import to Database</>)}
                    </Button>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${lowerBrand}_products.xlsx`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                      data-testid={`download-${lowerBrand}-xlsx`}
                    >
                      <Download className="w-4 h-4" /> Download Excel
                    </a>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${lowerBrand}_products.json`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                      data-testid={`download-${lowerBrand}-json`}
                    >
                      <Download className="w-4 h-4" /> Download JSON
                    </a>
                  </div>
                  {importResult[brand] && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800" data-testid={`brand-import-result-${lowerBrand}`}>
                      ✓ Imported {importResult[brand].imported} products (replaced {importResult[brand].deleted} existing). Edit any product in the Products tab to fix categories or descriptions.
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Upload Section */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-md font-semibold mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#FF6B35]" />
                Upload Excel Catalog
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Upload your Excel catalog to compare with online products. The system will identify:
                new products, price differences, category changes, and missing images.
              </p>
              
              <div className="flex items-center gap-4">
                <input
                  ref={catalogFileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    handleCatalogCompare(e.target.files?.[0]);
                    setSelectedNewProducts(new Set());
                    setSelectedDiffProducts(new Set());
                  }}
                  className="flex-1 border rounded-lg p-2 text-sm"
                  data-testid="catalog-file-input"
                />
                <Button 
                  onClick={() => catalogFileRef.current?.click()}
                  disabled={catalogLoading}
                  className="bg-[#0A2342]"
                  data-testid="catalog-compare-btn"
                >
                  {catalogLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Compare Catalog
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API_URL}/admin/products/export-excel`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'justdive_catalog.xlsx';
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('Catalog downloaded');
                    } catch { toast.error('Failed to download catalog'); }
                  }}
                  data-testid="catalog-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Current Catalog
                </Button>
              </div>
            </div>

            {/* Comparison Results */}
            {catalogComparison && (
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="catalog-summary-cards">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow" onClick={() => setCatalogDetailView('matched')}>
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-green-700">{catalogComparison.summary.total_matched}</div>
                    <div className="text-sm text-green-600">Matched</div>
                  </div>
                  <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${catalogDetailView === 'differences' ? 'ring-2 ring-yellow-400' : ''}`} onClick={() => setCatalogDetailView('differences')}>
                    <AlertTriangle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-yellow-700">{catalogComparison.summary.total_with_differences}</div>
                    <div className="text-sm text-yellow-600">Differences</div>
                  </div>
                  <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${catalogDetailView === 'new' ? 'ring-2 ring-blue-400' : ''}`} onClick={() => setCatalogDetailView('new')}>
                    <Plus className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-blue-700">{catalogComparison.summary.total_excel_only}</div>
                    <div className="text-sm text-blue-600">New in Excel</div>
                  </div>
                  <div className={`bg-red-50 border border-red-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${catalogDetailView === 'db_only' ? 'ring-2 ring-red-400' : ''}`} onClick={() => setCatalogDetailView('db_only')}>
                    <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-red-700">{catalogComparison.summary.total_db_only}</div>
                    <div className="text-sm text-red-600">DB Only</div>
                  </div>
                  <div className={`bg-purple-50 border border-purple-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${catalogDetailView === 'images' ? 'ring-2 ring-purple-400' : ''}`} onClick={() => setCatalogDetailView('images')}>
                    <ImageIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-purple-700">
                      {[...catalogComparison.matched, ...catalogComparison.differences, ...catalogComparison.db_only].filter(i => !i.has_image).length}
                    </div>
                    <div className="text-sm text-purple-600">Missing Images</div>
                  </div>
                </div>

                {/* New Categories Needed */}
                {catalogComparison.new_categories_needed?.length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800 mb-2">New Categories Will Be Created:</h4>
                    <div className="flex flex-wrap gap-2">
                      {catalogComparison.new_categories_needed.map((cat) => (
                        <Badge key={cat} className="bg-purple-600">{cat}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sync Actions */}
                <div className="bg-white rounded-lg border p-4" data-testid="catalog-sync-actions">
                  <h4 className="font-semibold mb-3">Sync Actions</h4>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      onClick={() => handleCatalogSync(true, false)}
                      disabled={catalogLoading || catalogComparison.summary.total_excel_only === 0}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="sync-create-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create {catalogComparison.summary.total_excel_only} New Products
                    </Button>
                    <Button 
                      onClick={() => handleCatalogSync(false, true)}
                      disabled={catalogLoading || catalogComparison.summary.total_with_differences === 0}
                      className="bg-yellow-600 hover:bg-yellow-700"
                      data-testid="sync-update-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Update {catalogComparison.summary.total_with_differences} Products
                    </Button>
                    <Button 
                      onClick={() => handleCatalogSync(true, true)}
                      disabled={catalogLoading}
                      className="bg-[#FF6B35] hover:bg-[#FF6B35]/90"
                      data-testid="sync-all-btn"
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Sync All (Create + Update)
                    </Button>
                  </div>
                </div>

                {/* Detail Views */}

                {/* Products with Differences */}
                {catalogDetailView === 'differences' && catalogComparison.differences?.length > 0 && (
                  <div className="bg-white rounded-lg border overflow-hidden" data-testid="catalog-differences-section">
                    <div className="bg-yellow-100 px-4 py-3 border-b flex items-center justify-between">
                      <h4 className="font-semibold text-yellow-800">Products with Differences ({catalogComparison.differences.length})</h4>
                      <label className="flex items-center gap-2 text-sm text-yellow-700 cursor-pointer">
                        <input 
                          type="checkbox"
                          checked={selectedDiffProducts.size === catalogComparison.differences.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDiffProducts(new Set(catalogComparison.differences.map((_, i) => i)));
                            } else {
                              setSelectedDiffProducts(new Set());
                            }
                          }}
                          className="rounded"
                        />
                        Select All
                      </label>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto">
                      {catalogComparison.differences.map((item, idx) => (
                        <div key={idx} className={`border-b p-4 hover:bg-gray-50 ${selectedDiffProducts.has(idx) ? 'bg-yellow-50' : ''}`}>
                          <div className="flex items-start gap-4">
                            <input 
                              type="checkbox"
                              checked={selectedDiffProducts.has(idx)}
                              onChange={(e) => {
                                const next = new Set(selectedDiffProducts);
                                e.target.checked ? next.add(idx) : next.delete(idx);
                                setSelectedDiffProducts(next);
                              }}
                              className="mt-1 rounded"
                            />
                            {/* Image thumbnail */}
                            <div className="w-16 h-16 flex-shrink-0 rounded border overflow-hidden bg-gray-100">
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                              ) : item.has_image ? (
                                <div className="w-full h-full flex items-center justify-center bg-green-50">
                                  <CheckCircle className="w-6 h-6 text-green-400" />
                                </div>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <ImageIcon className="w-6 h-6 text-gray-300" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium text-sm">{item.name}</span>
                                {!item.has_image && (
                                  <Badge variant="outline" className="border-red-300 text-red-600 text-xs">No Image</Badge>
                                )}
                              </div>
                              <div className="space-y-1">
                                {item.differences.map((diff, diffIdx) => (
                                  <div key={diffIdx} className="flex items-center gap-3 text-xs">
                                    <span className="font-medium capitalize w-20 text-gray-500">{diff.field}:</span>
                                    {diff.field === 'price' ? (
                                      <>
                                        <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded">Excel: R{diff.excel?.toFixed(2)}</span>
                                        <ArrowRight className="w-3 h-3 text-gray-400" />
                                        <span className="text-orange-600 bg-orange-50 px-2 py-0.5 rounded">DB: R{diff.db?.toFixed(2)}</span>
                                      </>
                                    ) : diff.field === 'description' ? (
                                      <span className="text-gray-600">{diff.note || 'Descriptions differ'} (Excel: {diff.excel_length} chars, DB: {diff.db_length} chars)</span>
                                    ) : (
                                      <>
                                        <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded">Excel: {diff.excel || diff.excel_normalized || '-'}</span>
                                        <ArrowRight className="w-3 h-3 text-gray-400" />
                                        <span className="text-orange-600 bg-orange-50 px-2 py-0.5 rounded">DB: {diff.db || '-'}</span>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                            {/* Image upload button */}
                            {!item.has_image && item.db_id && (
                              <div className="flex-shrink-0">
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    className="hidden"
                                    onChange={(e) => handleCatalogImageUpload(item.db_id, e.target.files?.[0])}
                                  />
                                  <div className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs border ${catalogImageUploading === item.db_id ? 'opacity-50' : 'hover:bg-gray-100 cursor-pointer'}`}>
                                    {catalogImageUploading === item.db_id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Upload className="w-3 h-3" />
                                    )}
                                    Upload Image
                                  </div>
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {catalogDetailView === 'differences' && catalogComparison.differences?.length === 0 && (
                  <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                    <p>No differences found between Excel and database products.</p>
                  </div>
                )}

                {/* New Products (Excel Only) */}
                {catalogDetailView === 'new' && catalogComparison.excel_only?.length > 0 && (
                  <div className="bg-white rounded-lg border overflow-hidden" data-testid="catalog-new-section">
                    <div className="bg-blue-100 px-4 py-3 border-b flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-blue-800">New Products in Excel ({catalogComparison.excel_only.length})</h4>
                        <p className="text-xs text-blue-600">These products will be created without images - upload manually after sync</p>
                      </div>
                      <label className="flex items-center gap-2 text-sm text-blue-700 cursor-pointer">
                        <input 
                          type="checkbox"
                          checked={selectedNewProducts.size === catalogComparison.excel_only.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedNewProducts(new Set(catalogComparison.excel_only.map((_, i) => i)));
                            } else {
                              setSelectedNewProducts(new Set());
                            }
                          }}
                          className="rounded"
                        />
                        Select All
                      </label>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="w-8 p-2"></th>
                            <th className="text-left p-2">Row</th>
                            <th className="text-left p-2">Name</th>
                            <th className="text-left p-2">Category</th>
                            <th className="text-right p-2">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {catalogComparison.excel_only.map((item, idx) => (
                            <tr key={idx} className={`border-b hover:bg-gray-50 ${selectedNewProducts.has(idx) ? 'bg-blue-50' : ''}`}>
                              <td className="p-2">
                                <input 
                                  type="checkbox"
                                  checked={selectedNewProducts.has(idx)}
                                  onChange={(e) => {
                                    const next = new Set(selectedNewProducts);
                                    e.target.checked ? next.add(idx) : next.delete(idx);
                                    setSelectedNewProducts(next);
                                  }}
                                  className="rounded"
                                />
                              </td>
                              <td className="p-2 text-gray-500">{item.excel_row}</td>
                              <td className="p-2 font-medium">{item.name}</td>
                              <td className="p-2">
                                <Badge variant="outline">{item.normalized_category || item.category}</Badge>
                              </td>
                              <td className="p-2 text-right">R{item.price?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {catalogDetailView === 'new' && catalogComparison.excel_only?.length === 0 && (
                  <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                    <p>No new products found in the Excel file.</p>
                  </div>
                )}

                {/* DB Only Products */}
                {catalogDetailView === 'db_only' && catalogComparison.db_only?.length > 0 && (
                  <div className="bg-white rounded-lg border overflow-hidden" data-testid="catalog-dbonly-section">
                    <div className="bg-red-100 px-4 py-3 border-b">
                      <h4 className="font-semibold text-red-800">Products Only in Database ({catalogComparison.db_only.length})</h4>
                      <p className="text-xs text-red-600">These products exist online but are not in your Excel file</p>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto">
                      {catalogComparison.db_only.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-3 border-b hover:bg-gray-50">
                          <div className="w-12 h-12 flex-shrink-0 rounded border overflow-hidden bg-gray-100">
                            {item.image_url ? (
                              <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                            ) : item.has_image ? (
                              <div className="w-full h-full flex items-center justify-center bg-green-50">
                                <CheckCircle className="w-5 h-5 text-green-400" />
                              </div>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon className="w-5 h-5 text-gray-300" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-sm truncate block">{item.name}</span>
                            <Badge variant="outline" className="text-xs mt-1">{item.category}</Badge>
                          </div>
                          <div className="text-right text-sm font-medium">R{item.price?.toFixed(2)}</div>
                          <div className="flex-shrink-0">
                            {item.has_image ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  className="hidden"
                                  onChange={(e) => handleCatalogImageUpload(item.id, e.target.files?.[0])}
                                />
                                <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs border ${catalogImageUploading === item.id ? 'opacity-50' : 'hover:bg-gray-100 cursor-pointer'}`}>
                                  {catalogImageUploading === item.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Upload className="w-3 h-3" />
                                  )}
                                  Upload
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {catalogDetailView === 'db_only' && catalogComparison.db_only?.length === 0 && (
                  <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                    <p>All database products have matching entries in the Excel file.</p>
                  </div>
                )}

                {/* Matched Products View */}
                {catalogDetailView === 'matched' && (
                  <div className="bg-white rounded-lg border overflow-hidden" data-testid="catalog-matched-section">
                    <div className="bg-green-100 px-4 py-3 border-b">
                      <h4 className="font-semibold text-green-800">Matched Products ({catalogComparison.matched.length})</h4>
                      <p className="text-xs text-green-600">Products that match between Excel and database</p>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto">
                      {catalogComparison.matched.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-3 border-b hover:bg-gray-50">
                          <div className="w-10 h-10 flex-shrink-0 rounded border overflow-hidden bg-gray-100">
                            {item.image_url ? (
                              <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                            ) : item.has_image ? (
                              <div className="w-full h-full flex items-center justify-center bg-green-50">
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              </div>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon className="w-4 h-4 text-gray-300" />
                              </div>
                            )}
                          </div>
                          <span className="font-medium text-sm flex-1">{item.name}</span>
                          <div className="flex-shrink-0">
                            {item.has_image ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  className="hidden"
                                  onChange={(e) => handleCatalogImageUpload(item.db_id, e.target.files?.[0])}
                                />
                                <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs border ${catalogImageUploading === item.db_id ? 'opacity-50' : 'hover:bg-gray-100 cursor-pointer'}`}>
                                  {catalogImageUploading === item.db_id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Upload className="w-3 h-3" />
                                  )}
                                  Upload
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Missing Images View */}
                {catalogDetailView === 'images' && (
                  <div className="bg-white rounded-lg border overflow-hidden" data-testid="catalog-images-section">
                    <div className="bg-orange-100 px-4 py-3 border-b">
                      <h4 className="font-semibold text-orange-800">Products Missing Images</h4>
                      <p className="text-xs text-orange-600">Upload images directly for these products</p>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto">
                      {[...catalogComparison.matched, ...catalogComparison.differences, ...catalogComparison.db_only]
                        .filter(item => !item.has_image)
                        .map((item, idx) => {
                          const productId = item.db_id || item.id;
                          return (
                            <div key={idx} className="flex items-center gap-4 p-3 border-b hover:bg-gray-50">
                              <div className="w-16 h-16 flex-shrink-0 rounded border-2 border-dashed border-orange-300 overflow-hidden bg-orange-50 flex items-center justify-center">
                                {item.image_url ? (
                                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                ) : item.has_image ? (
                                  <CheckCircle className="w-6 h-6 text-green-400" />
                                ) : (
                                  <ImageIcon className="w-6 h-6 text-orange-300" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-sm block truncate">{item.name}</span>
                                {item.differences && (
                                  <span className="text-xs text-yellow-600">Has field differences</span>
                                )}
                              </div>
                              {productId && (
                                <label className="cursor-pointer flex-shrink-0">
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    className="hidden"
                                    onChange={(e) => handleCatalogImageUpload(productId, e.target.files?.[0])}
                                  />
                                  <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 border-orange-400 text-orange-700 ${catalogImageUploading === productId ? 'opacity-50' : 'hover:bg-orange-50 cursor-pointer'}`}>
                                    {catalogImageUploading === productId ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Upload className="w-4 h-4" />
                                    )}
                                    Upload Image
                                  </div>
                                </label>
                              )}
                            </div>
                          );
                        })
                      }
                      {[...catalogComparison.matched, ...catalogComparison.differences, ...catalogComparison.db_only].filter(item => !item.has_image).length === 0 && (
                        <div className="p-8 text-center text-green-600">
                          <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                          <p>All products have images!</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sync Results */}
            {catalogSyncResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="catalog-sync-result">
                <h4 className="font-semibold text-green-800 mb-2">Sync Complete!</h4>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-green-700">{catalogSyncResult.summary.created}</div>
                    <div className="text-sm text-green-600">Created</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-700">{catalogSyncResult.summary.updated}</div>
                    <div className="text-sm text-blue-600">Updated</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-700">{catalogSyncResult.summary.skipped}</div>
                    <div className="text-sm text-gray-600">Skipped</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-700">{catalogSyncResult.summary.errors}</div>
                    <div className="text-sm text-red-600">Errors</div>
                  </div>
                </div>
                {catalogSyncResult.details.created.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-green-700 mb-1">New products created (need images):</p>
                    <div className="flex flex-wrap gap-1">
                      {catalogSyncResult.details.created.slice(0, 20).map((item, idx) => (
                        <Badge key={idx} className="bg-green-600 text-xs">{item.name}</Badge>
                      ))}
                      {catalogSyncResult.details.created.length > 20 && (
                        <Badge className="bg-green-400">+{catalogSyncResult.details.created.length - 20} more</Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
  );
};

export default CatalogSyncTab;
